// 프로그래매틱 SEO 카테고리 페이지용 동결 스냅샷 로더.
// public/assets/ac-examples/categories.json (런타임 LLM 0 · 프리렌더 가능).
//
// 축 4개:
//   type     재해형태   /ac-datacenter/t/{slug}
//   agent    기인물     /ac-datacenter/a/{slug}
//   industry 업종·공정  /ac-datacenter/i/{slug}          ← 신설
//   cross    업종×재해형태 /ac-datacenter/i/{ind}/t/{type} ← 신설(롱테일 핵심)
import type { AcCase, AcAggBucket } from './acDataCenterApi'

export type AcAxis = 'type' | 'agent' | 'industry' | 'cross'

export interface AcCategory {
  slug: string
  axis: AcAxis
  label: { ko: string; en: string }
  keywords: string[]
  related_works: string[]
  intro: { ko?: string; en?: string }
  prevention: { ko?: string[]; en?: string[] }
  cases: { ko?: AcCase[]; en?: AcCase[] }
  agg: { ko?: { by_accident_type: AcAggBucket[]; by_cause: AcAggBucket[] }; en?: { by_accident_type: AcAggBucket[]; by_cause: AcAggBucket[] } }
  /** cross 전용 — 상위 축(내부링크·브레드크럼) */
  industry?: string
  type?: string
}

interface CategoriesFile {
  types: AcCategory[]
  agents: AcCategory[]
  industries: AcCategory[]
  crosses: AcCategory[]
}

let _cache: CategoriesFile | null = null

export async function loadCategories(): Promise<CategoriesFile> {
  if (_cache) return _cache
  const res = await fetch('/assets/ac-examples/categories.json')
  if (!res.ok) throw new Error('categories load failed')
  const raw = (await res.json()) as Partial<CategoriesFile>
  _cache = {
    types: raw.types ?? [],
    agents: raw.agents ?? [],
    industries: raw.industries ?? [],
    crosses: raw.crosses ?? [],
  }
  return _cache
}

function bucket(file: CategoriesFile, axis: AcAxis): AcCategory[] {
  switch (axis) {
    case 'type': return file.types
    case 'agent': return file.agents
    case 'industry': return file.industries
    case 'cross': return file.crosses
  }
}

export function findCategory(file: CategoriesFile, axis: AcAxis, slug: string): AcCategory | null {
  return bucket(file, axis).find((c) => c.slug === slug) || null
}

/** URL 경로 세그먼트 — 라우트와 canonical 이 같은 규칙을 쓰도록 한 곳에서 만든다. */
export function categoryPath(cat: AcCategory): string {
  switch (cat.axis) {
    case 'type': return `/ac-datacenter/t/${cat.slug}`
    case 'agent': return `/ac-datacenter/a/${cat.slug}`
    case 'industry': return `/ac-datacenter/i/${cat.slug}`
    case 'cross': return `/ac-datacenter/i/${cat.industry}/t/${cat.type}`
  }
}

/** 이 업종에서 실제로 만들어진 교차 페이지들(내부링크용). */
export function crossesOfIndustry(file: CategoriesFile, industry: string): AcCategory[] {
  return file.crosses.filter((c) => c.industry === industry)
}

/** 이 재해형태를 다루는 업종별 교차 페이지들(내부링크용). */
export function crossesOfType(file: CategoriesFile, type: string): AcCategory[] {
  return file.crosses.filter((c) => c.type === type)
}
